import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import * as Yup from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import axios from "../../../utills/api"

const TASK_API = "task";
const DEPT_API = "department/department";
const TEAM_API = "team";

const ManageTask = () => {
  document.title = "HMS | Task";

  // State
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [taskModal, setTaskModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filteredMembers, setFilteredMembers] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("");

  // Modal toggle
  const toggleTaskModal = useCallback(() => {
    setTaskModal(!taskModal);
    if (!taskModal) setSelectedTask(null);
  }, [taskModal]);

  // Fetch departments & teams
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get("department/department", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setDepartments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching departments:", err);
      toast.error("Failed to fetch departments!");
    }
  };


  const fetchTeams = async () => {
    try {
      const res = await axios.get(TEAM_API);
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setTeams(data);
      setFilteredMembers(data);
    } catch (error) {
      toast.error("Failed to fetch team members!");
      console.error("Error fetching team members:", error);
    }
  };

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      const res = await axios.get(TASK_API);
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setTasks(data);
    } catch (error) {
      toast.error("Failed to fetch tasks!");
      console.error("Error fetching tasks:", error);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchTeams();
    fetchTasks();
  }, []);

  // Formik
  const taskForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      task: selectedTask?.task || "",
      dueDate: selectedTask?.dueDate || "",
      status: selectedTask?.status || "New",
      priority: selectedTask?.priority || "Medium",
      department: selectedTask?.department || "All",
      assignedTo: selectedTask?.assignedTo
        ? Array.isArray(selectedTask.assignedTo)
          ? selectedTask.assignedTo
          : [selectedTask.assignedTo]
        : [],
    },
    validationSchema: Yup.object({
      task: Yup.string().required("Please enter task"),
      dueDate: Yup.string().required("Please select due date"),
      assignedTo: Yup.array()
        .min(1, "Select at least one member")
        .required("Please assign to at least one member"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          assignedTo: values.assignedTo, // array of selected names
        };
        if (isEdit && selectedTask) {
          await axios.put(`${TASK_API}/${selectedTask._id}`, payload);
          toast.success("Task updated successfully");
        } else {
          await axios.post(TASK_API, payload);
          toast.success("Task added successfully");
        }
        toggleTaskModal();
        fetchTasks();
      } catch (err) {
        console.error("Error saving task:", err);
        toast.error("Failed to save task");
      }
    },
  });

  // Update filteredMembers based on department
  useEffect(() => {
    if (!taskForm.values.department || taskForm.values.department === "All") {
      setFilteredMembers(teams); // show all members
    } else {
      const filtered = teams.filter(
        (t) => t.department === taskForm.values.department
      );
      setFilteredMembers(filtered);
    }
    // reset assignedTo if current selections are not in filtered list
    taskForm.setFieldValue(
      "assignedTo",
      taskForm.values.assignedTo.filter((val) =>
        filteredMembers.some((m) => m.name === val)
      )
    );
  }, [taskForm.values.department, teams]);

  // Delete task
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await axios.delete(`${TASK_API}/${id}`);
      toast.success("Task deleted!");
      fetchTasks();
    } catch (err) {
      console.error("Error deleting task:", err);
      toast.error("Failed to delete task");
    }
  };

  // Columns
  const columns = useMemo(
    () => [
      { header: "Task", accessorKey: "task" },
      {
        header: "Department",
        accessorKey: "department",
        cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        header: "Assigned To",
        accessorKey: "assignedTo",
        cell: ({ cell }) => (cell.getValue().join ? cell.getValue().join(", ") : "—"),
      },
      {
        header: "Due Date",
        accessorKey: "dueDate",
        cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString()
            : "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ cell }) => (
          <span
            className={`badge ${cell.getValue() === "Completed"
                ? "bg-success-subtle text-success"
                : "bg-warning-subtle text-warning"
              } text-uppercase`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Priority",
        accessorKey: "priority",
        cell: ({ cell }) => (
          <span
            className={`badge ${cell.getValue() === "High"
                ? "bg-danger"
                : cell.getValue() === "Medium"
                  ? "bg-warning"
                  : "bg-success"
              } text-uppercase`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <>
            <td className="d-flex gap-2">
              <Link
                onClick={() => {
                  setIsEdit(true);
                  setSelectedTask(row.original);
                  setTaskModal(true);
                }}
                className="btn btn-sm btn-soft-primary"
                style={{ width: "28px", height: "28px" }}
              >
                <i className="ri-edit-line"></i>
              </Link>


              <Link
                onClick={() => handleDelete(row.original._id)}
                className="btn btn-sm btn-soft-danger"
                style={{ width: "28px", height: "28px" }}
              >
                <i className="las la-trash-alt"></i>
              </Link>
            </td>


          </>
        )
      }

    ],
    [toggleTaskModal]
  );

  // Filtered data
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    if (searchTerm) {
      filtered = filtered.filter((t) =>
        t.task?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }
    if (sortOption === "By Name") {
      filtered = filtered.sort((a, b) => a.task.localeCompare(b.task));
    }
    return filtered;
  }, [tasks, searchTerm, statusFilter, sortOption]);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Manage Task" pageTitle="Tasks" />

          <Row>
            <Col>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Tasks</h5>
                  <Button
                    color="primary"
                    onClick={() => {
                      setIsEdit(false);
                      toggleTaskModal();
                    }}
                  >
                    Add Task
                  </Button>
                </div>
                <div className="card-body">
                  <Row className="g-2 mb-3">
                    <Col lg="3">
                      <Input
                        type="text"
                        placeholder="Search Task..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col lg="3">
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="New">New</option>
                        <option value="Inprogress">Inprogress</option>
                        <option value="Completed">Completed</option>
                      </Input>
                    </Col>
                    <Col lg="3">
                      <Input
                        type="select"
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                      >
                        <option value="">Sort</option>
                        <option value="By Name">By Name</option>
                      </Input>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <TableContainer
                      columns={columns}
                      data={filteredTasks}
                      customPageSize={10}
                      isGlobalFilter={false}
                      isPagination
                      className="text-nowrap"
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add/Edit Task Modal */}
      <Modal isOpen={taskModal} toggle={toggleTaskModal} centered>
        <ModalHeader toggle={toggleTaskModal}>
          {isEdit ? "Edit Task" : "Add Task"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={taskForm.handleSubmit}>
            {/* Task Name */}
            <div className="mb-3">
              <label className="form-label">Task Name</label>
              <Input
                type="text"
                name="task"
                value={taskForm.values.task}
                onChange={taskForm.handleChange}
                onBlur={taskForm.handleBlur}
                invalid={taskForm.touched.task && !!taskForm.errors.task}
              />
              {taskForm.touched.task && taskForm.errors.task && (
                <FormFeedback>{taskForm.errors.task}</FormFeedback>
              )}
            </div>

            {/* Due Date */}
            <div className="mb-3">
              <label className="form-label">Due Date</label>
              <Flatpickr
                name="dueDate"
                className="form-control"
                options={{
                  altInput: "",
                  altFormat: "d M, Y",
                  dateFormat: "Y-m-d",
                }}
                value={taskForm.values.dueDate}
                onChange={(date) => taskForm.setFieldValue("dueDate", date[0])}
              />
            </div>

            {/* Status */}
            <div className="mb-3">
              <label className="form-label">Status</label>
              <Input
                type="select"
                name="status"
                value={taskForm.values.status}
                onChange={taskForm.handleChange}
              >
                <option value="New">New</option>
                <option value="Inprogress">Inprogress</option>
                <option value="Completed">Completed</option>
              </Input>
            </div>

            {/* Priority */}
            <div className="mb-3">
              <label className="form-label">Priority</label>
              <Input
                type="select"
                name="priority"
                value={taskForm.values.priority}
                onChange={taskForm.handleChange}
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Input>
            </div>

            {/* Department */}
            <div className="mb-3">
              <label className="form-label">Department</label>
              <Input
                type="select"
                name="department"
                value={taskForm.values.department}
                onChange={taskForm.handleChange}
              >
                <option value="All">All</option>
                {departments.map((d) => (
                  <option key={d._id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </Input>
            </div>

            {/* Assign To (multiple selection) */}
            <div className="mb-3">
              <label className="form-label">Assign To</label>
              <Select
                isMulti
                name="assignedTo"
                options={filteredMembers.map((t) => ({ value: t.name, label: t.name }))}
                value={taskForm.values.assignedTo.map((name) => ({ value: name, label: name }))}
                onChange={(selectedOptions) =>
                  taskForm.setFieldValue(
                    "assignedTo",
                    selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                  )
                }
                placeholder="Assigned To"
              />
              {taskForm.touched.assignedTo && taskForm.errors.assignedTo && (
                <FormFeedback className="d-block">{taskForm.errors.assignedTo}</FormFeedback>
              )}
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Button color="secondary" onClick={toggleTaskModal}>
                Close
              </Button>
              <Button color="primary" type="submit">
                {isEdit ? "Save" : "Add Task"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ManageTask;

